import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import Layout from './Layout.jsx'
import Icon from './icons.jsx'
import SupportModal from './SupportModal.jsx'
import { useApp } from '../context/AppContext.jsx'
import { canManageApiKeys } from '../lib/apiKeys.js'

/**
 * Área de Configurações — casca compartilhada por "Meu perfil", "Clientes" e
 * "Conexões".
 *
 * - Reusa o <Layout> do sistema (sidebar principal + topbar). Enquanto a área
 *   está montada, a sidebar principal é MINIMIZADA via
 *   `setSidebarForcedCollapsed(true)` (mecanismo efêmero, não sobrescreve a
 *   preferência manual e é revertido ao sair).
 * - Renderiza uma sidebar secundária full-height, exclusiva desta área, com o
 *   título "Configurações" e a navegação entre as três telas. Como é um
 *   <Outlet> sob uma rota-pai única, trocar de tela NÃO desmonta esta casca →
 *   navegação instantânea e a sidebar principal continua minimizada.
 */
const SETTINGS_NAV = [
  { to: '/configuracoes/perfil', label: 'Meu perfil', icon: 'user' },
  { to: '/configuracoes/clientes', label: 'Clientes', icon: 'users' },
  { to: '/configuracoes/usuarios', label: 'Usuários', icon: 'user' },
  { to: '/configuracoes/conexoes', label: 'Conexões', icon: 'link' },
  { to: '/configuracoes/api', label: 'API', icon: 'key', adminOnly: true },
]

const TITLES = {
  '/configuracoes/perfil': 'Meu perfil',
  '/configuracoes/clientes': 'Clientes',
  '/configuracoes/usuarios': 'Usuários',
  '/configuracoes/conexoes': 'Conexões',
  '/configuracoes/api': 'API',
}

export default function SettingsLayout() {
  const { setSidebarForcedCollapsed, memberships } = useApp()
  const canSeeApi = canManageApiKeys(memberships || [])
  const { pathname } = useLocation()
  const [supportOpen, setSupportOpen] = useState(false)

  useEffect(() => {
    setSidebarForcedCollapsed(true)
    return () => setSidebarForcedCollapsed(false)
  }, [setSidebarForcedCollapsed])

  return (
    <Layout
      title={TITLES[pathname] || 'Configurações'}
      breadcrumb={[{ label: 'Sistema' }, { label: TITLES[pathname] || 'Configurações' }]}
    >
      <div className="settings-shell">
        <aside className="settings-nav">
          <div className="settings-nav-title">Configurações</div>
          {SETTINGS_NAV.filter((item) => !item.adminOnly || canSeeApi).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `settings-nav-item${isActive ? ' active' : ''}`}
            >
              <span className="sb-item-icon"><Icon n={item.icon} s={17} /></span>
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* Suporte — último item, logo abaixo de Conexões. Abre o mesmo modal
              de canais de atendimento que antes vivia na sidebar principal; por
              ser um diálogo (e não uma tela), é <button> e não <NavLink>. */}
          <button
            type="button"
            className="settings-nav-item"
            aria-haspopup="dialog"
            onClick={() => setSupportOpen(true)}
          >
            <span className="sb-item-icon"><Icon n="support" s={17} /></span>
            <span>Suporte</span>
          </button>
        </aside>

        <div className="settings-panel">
          <Outlet />
        </div>
      </div>

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </Layout>
  )
}
